import React from 'react';
import { Clock, Bell, AlertTriangle, Timer, Settings as SettingsIcon } from 'lucide-react';
import { SystemConfig, RetentionMonitorConfig } from '../../types';

interface Props {
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
}

export const SettingsTabRetentionMonitor: React.FC<Props> = ({ config, setConfig }) => {
  const monitorConfig = config.retentionMonitorConfig || {
    productionMarginMinutes: 3,
    dispatchHoldToleranceMulti: 10,
    dispatchHoldMarginMinutes: 3,
    checkIntervalMs: 10000,
    warnCooldownMs: 60000,
  };

  const updateMonitorConfig = (updates: Partial<RetentionMonitorConfig>) => {
    setConfig({
      ...config,
      retentionMonitorConfig: {
        ...monitorConfig,
        ...updates,
      },
    });
  };

  return (
    <div className="p-6 space-y-8 overflow-y-auto h-full">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-border-dim">
        <div className="p-2.5 bg-sal-burnt-orange rounded-xl shadow-lg">
          <Clock className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-black text-text-primary uppercase tracking-tight">
            Monitor de Retenção
          </h2>
          <p className="text-xs text-text-muted font-medium">
            Configure os alertas para liberação de pedidos retidos
          </p>
        </div>
      </div>

      {/* Production Retention */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Timer className="w-4 h-4 text-sal-burnt-orange" />
          <h3 className="text-sm font-black text-text-primary uppercase tracking-wide">
            Retenção de Produção
          </h3>
        </div>
        
        <div className="bg-card-bg rounded-xl p-4 border border-border-dim space-y-4">
          <div>
            <label htmlFor="productionMargin" className="block text-xs font-bold text-text-secondary mb-2">
              Margem de Segurança (minutos)
            </label>
            <div className="flex items-center gap-3">
              <input
                id="productionMargin"
                type="range"
                min="1"
                max="15"
                step="1"
                value={monitorConfig.productionMarginMinutes}
                onChange={(e) => updateMonitorConfig({ 
                  productionMarginMinutes: parseInt(e.target.value) 
                })}
                className="flex-1 accent-sal-burnt-orange"
              />
              <input
                type="number"
                min="1"
                max="15"
                value={monitorConfig.productionMarginMinutes}
                onChange={(e) => updateMonitorConfig({ 
                  productionMarginMinutes: parseInt(e.target.value) || 3 
                })}
                className="w-16 px-2 py-1.5 bg-input-bg border border-border-dim rounded-lg text-center text-sm font-bold text-text-primary"
              />
            </div>
            <p className="text-xs text-text-muted mt-1">
              Tempo antes do alerta quando o pedido deve ser liberado
            </p>
          </div>
        </div>
      </section>

      {/* Dispatch Hold */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-sal-burnt-orange" />
          <h3 className="text-sm font-black text-text-primary uppercase tracking-wide">
            Bloqueio de Despacho
          </h3>
        </div>
        
        <div className="bg-card-bg rounded-xl p-4 border border-border-dim space-y-4">
          <div>
            <label htmlFor="dispatchTolerance" className="block text-xs font-bold text-text-secondary mb-2">
              Tolerância Extra por Pedido Adicional (minutos)
            </label>
            <div className="flex items-center gap-3">
              <input
                id="dispatchTolerance"
                type="range"
                min="0"
                max="30"
                step="5"
                value={monitorConfig.dispatchHoldToleranceMulti}
                onChange={(e) => updateMonitorConfig({ 
                  dispatchHoldToleranceMulti: parseInt(e.target.value) 
                })}
                className="flex-1 accent-sal-burnt-orange"
              />
              <input
                type="number"
                min="0"
                max="30"
                value={monitorConfig.dispatchHoldToleranceMulti}
                onChange={(e) => updateMonitorConfig({ 
                  dispatchHoldToleranceMulti: parseInt(e.target.value) || 0 
                })}
                className="w-16 px-2 py-1.5 bg-input-bg border border-border-dim rounded-lg text-center text-sm font-bold text-text-primary"
              />
            </div>
            <p className="text-xs text-text-muted mt-1">
              Tempo extra de tolerância para cada pedido adicional na rota
            </p>
          </div>

          <div>
            <label htmlFor="dispatchMargin" className="block text-xs font-bold text-text-secondary mb-2">
              Margem de Segurança (minutos)
            </label>
            <div className="flex items-center gap-3">
              <input
                id="dispatchMargin"
                type="range"
                min="1"
                max="15"
                step="1"
                value={monitorConfig.dispatchHoldMarginMinutes}
                onChange={(e) => updateMonitorConfig({ 
                  dispatchHoldMarginMinutes: parseInt(e.target.value) 
                })}
                className="flex-1 accent-sal-burnt-orange"
              />
              <input
                type="number"
                min="1"
                max="15"
                value={monitorConfig.dispatchHoldMarginMinutes}
                onChange={(e) => updateMonitorConfig({ 
                  dispatchHoldMarginMinutes: parseInt(e.target.value) || 3 
                })}
                className="w-16 px-2 py-1.5 bg-input-bg border border-border-dim rounded-lg text-center text-sm font-bold text-text-primary"
              />
            </div>
            <p className="text-xs text-text-muted mt-1">
              Tempo antes do alerta quando o bloqueio deve ser liberado
            </p>
          </div>
        </div>
      </section>

      {/* Comportamento */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <SettingsIcon className="w-4 h-4 text-sal-burnt-orange" />
          <h3 className="text-sm font-black text-text-primary uppercase tracking-wide">
            Comportamento
          </h3>
        </div>
        
        <div className="bg-card-bg rounded-xl p-4 border border-border-dim space-y-4">
          <div>
            <label htmlFor="checkInterval" className="block text-xs font-bold text-text-secondary mb-2">
              Intervalo de Verificação (segundos)
            </label>
            <div className="flex items-center gap-3">
              <input
                id="checkInterval"
                type="range"
                min="5"
                max="60"
                step="5"
                value={monitorConfig.checkIntervalMs / 1000}
                onChange={(e) => updateMonitorConfig({ 
                  checkIntervalMs: parseInt(e.target.value) * 1000 
                })}
                className="flex-1 accent-sal-burnt-orange"
              />
              <input
                type="number"
                min="5"
                max="60"
                value={monitorConfig.checkIntervalMs / 1000}
                onChange={(e) => updateMonitorConfig({ 
                  checkIntervalMs: (parseInt(e.target.value) || 10) * 1000 
                })}
                className="w-16 px-2 py-1.5 bg-input-bg border border-border-dim rounded-lg text-center text-sm font-bold text-text-primary"
              />
            </div>
            <p className="text-xs text-text-muted mt-1">
              Com que frequência o sistema verifica os pedidos retidos
            </p>
          </div>

          <div>
            <label htmlFor="warnCooldown" className="block text-xs font-bold text-text-secondary mb-2">
              Tempo Entre Avisos (segundos)
            </label>
            <div className="flex items-center gap-3">
              <input
                id="warnCooldown"
                type="range"
                min="30"
                max="300"
                step="30"
                value={monitorConfig.warnCooldownMs / 1000}
                onChange={(e) => updateMonitorConfig({ 
                  warnCooldownMs: parseInt(e.target.value) * 1000 
                })}
                className="flex-1 accent-sal-burnt-orange"
              />
              <input
                type="number"
                min="30"
                max="300"
                value={monitorConfig.warnCooldownMs / 1000}
                onChange={(e) => updateMonitorConfig({ 
                  warnCooldownMs: (parseInt(e.target.value) || 60) * 1000 
                })}
                className="w-16 px-2 py-1.5 bg-input-bg border border-border-dim rounded-lg text-center text-sm font-bold text-text-primary"
              />
            </div>
            <p className="text-xs text-text-muted mt-1">
              Tempo mínimo entre alertas repetidos para o mesmo pedido
            </p>
          </div>
        </div>
      </section>

      {/* Preview */}
      <section className="bg-sal-burnt-orange/10 rounded-xl p-4 border border-sal-burnt-orange/30">
        <div className="flex items-start gap-3">
          <Bell className="w-5 h-5 text-sal-burnt-orange shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-bold text-text-primary mb-1">
              Como funciona
            </h4>
            <ul className="text-xs text-text-muted space-y-1">
              <li>• Quando um pedido tem estratégia de retenção confirmada, o sistema monitora automaticamente</li>
              <li>• <strong>Aviso amarelo</strong>: {monitorConfig.productionMarginMinutes} min antes do prazo</li>
              <li>• <strong>Alerta vermelho</strong>: Quando o prazo já passou - LIBERAR AGORA</li>
              <li>• Os alertas aparecem como toast e são anunciados pela Sofia</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};
